import React, { useState, useEffect } from 'react';
import { View, Text, Pressable, Modal, StyleSheet } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import AirbnbStyleCalendar from '../../../../components/calendar/AirbnbStyleCalendar';

interface DateRangeEditModalProps {
    visible: boolean;
    onClose: () => void;
    currentStartDate: string;
    currentEndDate: string;
    currentDaysOfWeek?: string[];
    onSave: (startDate: string, endDate: string, daysOfWeek: string[]) => void;
    sessionTitle?: string;
}

const DateRangeEditModal: React.FC<DateRangeEditModalProps> = ({
    visible,
    onClose,
    currentStartDate,
    currentEndDate,
    onSave,
    currentDaysOfWeek,
    sessionTitle = "Edit Date Range"
}) => {
    const [dates, setDates] = useState(() => {
        // Safely parse dates or use defaults
        const parseDate = (dateStr) => {
            if (!dateStr || dateStr === '') return new Date();
            const parsed = new Date(dateStr);
            return isNaN(parsed.getTime()) ? new Date() : parsed;
        };
        return [parseDate(currentStartDate), parseDate(currentEndDate)];
    });
    const [error, setError] = useState('');
    const allDays = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    const [selectedDays, setSelectedDays] = useState<string[]>(() => {
        if (currentDaysOfWeek && currentDaysOfWeek.length > 0) return currentDaysOfWeek;
        return allDays;
    });

    useEffect(() => {
        if (visible) {
            const parseDate = (dateStr) => {
                if (!dateStr || dateStr === '') return new Date();
                const parsed = new Date(dateStr);
                return isNaN(parsed.getTime()) ? new Date() : parsed;
            };
            const newDates = [parseDate(currentStartDate), parseDate(currentEndDate)];
            setDates(newDates);
            setError(validateDateRange(newDates));
            setSelectedDays(currentDaysOfWeek && currentDaysOfWeek.length > 0 ? currentDaysOfWeek : allDays);
        }
    }, [visible, currentStartDate, currentEndDate, currentDaysOfWeek]);

    const validateDateRange = (dateRange) => {
        if (!dateRange || dateRange.length !== 2) {
            return 'Please select both start and end dates';
        }
        const [startDate, endDate] = dateRange;
        
        if (!startDate || !endDate) {
            return 'Please select both start and end dates';
        }
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (startDate < today) {
            return 'Start date cannot be in the past';
        }
        
        if (endDate < startDate) {
            return 'End date must be after start date';
        }
        
        const diffTime = Math.abs(endDate - startDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays > 365) {
            return 'Session cannot be longer than 365 days';
        }
        
        return '';
    };

    const handleDateChange = (newDates) => {
        setDates(newDates);
        setError(validateDateRange(newDates));
    };

    const isValid = !error && dates && dates.length === 2 && selectedDays.length > 0;

    const handleSave = () => {
        const validationError = validateDateRange(dates);
        if (validationError) {
            setError(validationError);
            return;
        }
        const [startDate, endDate] = dates;
        onSave(startDate.toLocaleDateString(), endDate.toLocaleDateString(), selectedDays);
        onClose();
    };

    const handleCancel = () => {
        // Reset to original dates
        const parseDate = (dateStr) => {
            if (!dateStr || dateStr === '') return new Date();
            const parsed = new Date(dateStr);
            return isNaN(parsed.getTime()) ? new Date() : parsed;
        };
        setDates([parseDate(currentStartDate), parseDate(currentEndDate)]);
        onClose();
    };



    return (
        <Modal
            visible={visible}
            transparent={true}
            animationType="fade"
            onRequestClose={onClose}
        >
            <View style={styles.overlay}>
                <View style={styles.modalContainer}>
                    {/* Header */}
                    <View style={styles.header}>
                        <Text style={styles.title}>{sessionTitle}</Text>
                        <Pressable onPress={onClose} style={styles.closeButton}>
                            <MaterialCommunityIcons name="close" size={20} color="#666" />
                        </Pressable>
                    </View>

                    {/* Content */}
                    <View style={styles.content}>
                        <View style={styles.calendarContainer}>
                            <AirbnbStyleCalendar
                                startDate={dates[0]}
                                endDate={dates[1]}
                                onDateChange={(startDate, endDate) => {
                                    if (startDate && endDate) {
                                        handleDateChange([startDate, endDate]);
                                    }
                                }}
                                minDate={new Date()}
                            />
                        </View>
                        {/* Days of Week selector */}
                        <View style={styles.daysOfWeekContainer}>
                            <Text style={styles.daysOfWeekTitle}>Days of week</Text>
                            <View style={styles.daysRow}>
                                {allDays.map((day) => {
                                    const isSelected = selectedDays.includes(day);
                                    return (
                                        <Pressable
                                            key={day}
                                            onPress={() => {
                                                setSelectedDays(prev => {
                                                    const has = prev.includes(day);
                                                    if (has) {
                                                        const next = prev.filter(d => d !== day);
                                                        return next;
                                                    } else {
                                                        return [...prev, day];
                                                    }
                                                });
                                            }}
                                            style={[styles.dayButton, isSelected ? styles.dayButtonSelected : styles.dayButtonUnselected]}
                                        >
                                            <Text style={[styles.dayButtonText, isSelected ? styles.dayButtonTextSelected : styles.dayButtonTextUnselected]}>{day}</Text>
                                        </Pressable>
                                    );
                                })}
                            </View>
                            {selectedDays.length === 0 && (
                                <Text style={styles.daysWarning}>Select at least one day</Text>
                            )}
                        </View>
                        
                        {error && (
                            <Text style={styles.errorText}>{error}</Text>
                        )}
                    </View>

                    {/* Footer */}
                    <View style={styles.footer}>
                        <Pressable onPress={handleCancel} style={[styles.button, styles.cancelButton]}>
                            <Text style={styles.cancelButtonText}>Cancel</Text>
                        </Pressable>
                        <Pressable 
                            onPress={handleSave} 
                            style={[styles.button, isValid ? styles.saveButton : styles.saveButtonDisabled]}
                            disabled={!isValid}
                        >
                            <Text style={[styles.saveButtonText, !isValid && styles.saveButtonTextDisabled]}>Save</Text>
                        </Pressable>
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    modalContainer: {
        backgroundColor: 'white',
        borderRadius: 12,
        width: '100%',
        maxWidth: 700,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.25,
        shadowRadius: 12,
        elevation: 8,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#e9ecef',
    },
    title: {
        fontSize: 18,
        fontWeight: '600',
        color: '#333',
    },
    closeButton: {
        padding: 4,
    },
    content: {
        padding: 20,
    },
    calendarContainer: {
        position: 'relative',
        zIndex: 1,
    },
    daysOfWeekContainer: {
        marginTop: 16,
    },
    daysOfWeekTitle: {
        fontSize: 12,
        fontWeight: '600',
        color: '#333',
        marginBottom: 8,
    },
    daysRow: {
        flexDirection: 'row',
        flexWrap: 'wrap' as const,
        gap: 8,
    },
    dayButton: {
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 16,
        borderWidth: 1,
    },
    dayButtonSelected: {
        backgroundColor: '#000000',
        borderColor: '#000000',
    },
    dayButtonUnselected: {
        backgroundColor: '#f8f9fa',
        borderColor: '#e9ecef',
    },
    dayButtonText: {
        fontSize: 12,
        fontWeight: '600',
    },
    dayButtonTextSelected: {
        color: 'white',
    },
    dayButtonTextUnselected: {
        color: '#333',
    },
    daysWarning: {
        marginTop: 8,
        fontSize: 11,
        color: '#dc3545',
        textAlign: 'left',
    },
    errorText: {
        fontSize: 12,
        color: '#dc3545',
        marginTop: 16,
        textAlign: 'center',
        fontWeight: '500',
    },
    footer: {
        flexDirection: 'row',
        padding: 20,
        paddingTop: 0,
        gap: 12,
    },
    button: {
        flex: 1,
        paddingVertical: 12,
        borderRadius: 8,
        alignItems: 'center',
    },
    cancelButton: {
        backgroundColor: '#f8f9fa',
        borderWidth: 1,
        borderColor: '#e9ecef',
    },
    cancelButtonText: {
        fontSize: 16,
        fontWeight: '500',
        color: '#666',
    },
    saveButton: {
        backgroundColor: '#3C8248',
    },
    saveButtonDisabled: {
        backgroundColor: '#cccccc',
    },
    saveButtonText: {
        fontSize: 16,
        fontWeight: '500',
        color: 'white',
    },
    saveButtonTextDisabled: {
        color: '#666666',
    },
});

export default DateRangeEditModal; 